import axios from 'axios';
import { AuthUtils } from './AuthUtils';
import { getBrowserInfo } from './BrowserInfo';

export type AuthTelemetryEvent =
  | 'LoginSuccess'
  | 'RefreshTokenFailure'
  | 'ForcedLogout'
  | 'TokenExpired'
  | 'ClientReport';

interface IReportOptions {
  details?: Record<string, unknown>;
}

// Dedicated instance with no auth header and no interceptors: this must be safe
// to call during logout/refresh failure without triggering the 401 refresh loop.
const telemetryClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL,
});

export class AuthTelemetry {
  /**
   * Fire-and-forget report of a client-side auth event. Never throws so it can be
   * called from error paths without affecting the auth flow.
   */
  static async report(
    eventType: AuthTelemetryEvent,
    options: IReportOptions = {},
  ): Promise<void> {
    try {
      const user = AuthUtils.getLoggedUser();
      const browser = await getBrowserInfo();

      const details = {
        browser: browser.browser,
        userAgent: browser.userAgent,
        cookiesEnabled: browser.cookiesEnabled,
        localStorageAvailable: browser.localStorageAvailable,
        language: browser.language,
        platform: browser.platform,
        screen: browser.screen,
        url: window.location.href,
        hasRefreshToken: !!user.refreshToken,
        issuedDatetime: user.issuedDatetime,
        expiresIn: user.expiresIn,
        ...options.details,
      };

      await telemetryClient.post('/Account/LogAuthEvent', {
        EventType: eventType,
        UserId: user.userId,
        Email: user.email,
        TenantId: user.currentTenant,
        IsIncognito: browser.isIncognito,
        Details: JSON.stringify(details),
      });
    } catch {
      // Telemetry must never break the auth flow.
    }
  }
}
